import React, {
  Dispatch, useContext, useEffect, useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/PersonOutline';
import Modal from 'components/Modal';
import {
  ArchivedState,
  CreateProcessRequestInput,
  CreateProcessRequestMutation,
  FileData,
  GlobalProcessDuration,
  JobDescription,
  JobDescriptionType,
  ProcessRequestState,
  VisaStatus,
} from 'API';
import { useImmer } from 'use-immer';
import { nanoid } from 'nanoid';
import CardBase from 'components/CardBase';
import IconCircle from 'components/IconCircle';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import Tabs from 'components/Tabs';
import {
  createJobDescription,
  createProcessRequest,
  updateForeignNational,
} from 'graphql/mutations';
import ProcessService from 'services/process';
import PersonService from 'services/person';
import { DICTIONARY } from 'shared/constants';
import UserContext from 'shared/contexts/UserContext';
import useGetForeignNational from 'shared/hooks/useGetForeignNational';
import useGetCompany from 'shared/hooks/useGetCompany';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { isValidEmail, lower } from 'shared/utilities';
import { countryCodeResolver } from 'shared/geoResolvers';
import { buildGlobalSubCategory, globalVisaTypeLabel } from 'shared/globalProcess';
import AlertContext from 'shared/contexts/AlertContext';
import ContactsSidebarContext from 'shared/contexts/SidebarContext';
import NewIndividual from './NewIndividual';
import ExistingIndividual from './ExistingIndividual';
import EmploymentFields from '../inputSections/EmploymentFields';
import ProcessType from '../inputSections/ProcessType';
import SponsorshipType from '../inputSections/SponsorshipType';
import JobFields from '../inputSections/JobFields';
import GlobalProcessFields from '../inputSections/GlobalProcessFields';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';

const { generateSlug } = ProcessService;
const { fullname } = PersonService;

const getInitiateCaseStyles = (theme: Theme) => ({
  button: {
    display: 'block',
    margin: `${theme.spacing(4)} auto 0`,
  },
  nationalCard: {
    backgroundColor: theme.palette.common.white,
    marginBottom: '2rem',
    padding: '2rem',
  },
  sectionTitle: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: '1rem',
  },
  select: {
    width: '100%',
  },
  textField: {
    gridColumn: '2 / 3',
    width: '100%',
    paddingBottom: '1rem',
  },
  titleIcon: {
    marginRight: '1.0625rem',
  },
});

interface Props {
  open: boolean;
  setOpen: Dispatch<boolean>;
}

const InitiateCaseModal: React.FC<Props> = ({ open, setOpen }: Props) => {
  const theme = useTheme();
  const styles = getInitiateCaseStyles(theme);
  const [, setAlert] = useContext(AlertContext);
  const [userState] = useContext(UserContext);
  const [
    isExistingJob, setIsExistingJob] = useState<boolean | undefined>(undefined);
  const { person } = userState;
  const initialProcess: CreateProcessRequestInput = {
    slug: '',
    requestDate: '',
    state: ProcessRequestState.REQUESTED,
    initiatorID: person.id,
    employeeData: {
      firstname: '',
      lastname: '',
      tempID: nanoid(),
    },
    employmentData: {},
  };
  const { setNewItem } = useContext(ContactsSidebarContext);
  const [process, setProcess] = useImmer<CreateProcessRequestInput>(
    initialProcess,
  );
  const [resume, setResume] = useState<FileData>();
  const [uploadedDescription, setUploadedDescription] = useState<FileData>();
  const selectedNational = useGetForeignNational(process.foreignNationalID);
  const selectedCompany = useGetCompany(selectedNational?.companyID || process.companyID);
  const [emailIsTaken, setEmailIsTaken] = useState(false);
  const [titleIsTaken, setTitleIsTaken] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobDescription>();
  const [currentTab, setCurrentTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const handleAddPremiumProcessingChange = (event: any) => {
    setProcess((draft) => {
      draft.employmentData!.premiumProcessing = event.target.value === 'true';
    });
  };

  const handleNotesChange = (event: any) => {
    setProcess((draft) => {
      draft.employmentData!.notes = event.target.value;
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const oneOffJobID = nanoid();
      let name = '';
      if (selectedNational) {
        name = fullname(selectedNational);
      } else {
        const { firstname, lastname } = process.employeeData || {};
        name = `${firstname} ${lastname}`;
      }

      const slugBase = name + (process.requestedProcess
        ? ` ${process.requestedProcess === VisaStatus.NOT_SURE
          ? ' consultation' : process.requestedProcess}`
        : '');
      const slug = await generateSlug(slugBase);

      const requestDate = new Date().toISOString();

      // Database structure requires that we ensure companyID
      // and foreignNationalID do not get sent over as null.
      const { companyID, foreignNationalID, ...input } = process;

      // The global process subcategory is stored as free text (“Country - Visa Type”)
      // and is what every downstream screen — the request page, the case header,
      // activity emails — reads. Derive it here so the details captured on intake
      // don’t have to be entered a second time when the request is approved.
      const globalRequestedProcessSubCategory = process.requestedProcess === VisaStatus.GLOBAL_PROCESS
        && process.globalProcessCountryCode
        && process.globalVisaType
        ? buildGlobalSubCategory(
          countryCodeResolver(process.globalProcessCountryCode),
          globalVisaTypeLabel(process.globalVisaType),
        )
        : null;

      const assignedToID = selectedCompany?.accountManagerID || person.id;
      const newProcess = (await client.graphql({
        query: createProcessRequest,
        variables: {
          input: {
            ...input,
            ...(globalRequestedProcessSubCategory && { globalRequestedProcessSubCategory }),
            ...(foreignNationalID && { foreignNationalID }),
            ...(selectedNational?.companyID || companyID
              ? { companyID: selectedNational?.companyID || companyID }
              : {}
            ),
            assignedToID,
            assignedToEmployerID: assignedToID,
            assignedToTeamMemberID: assignedToID,
            requestDate,
            slug,
            jobDescriptionID: isExistingJob ? selectedJob!.id : oneOffJobID,
            lowerNationalName: selectedNational?.lowerFull
              || lower(`${process.employeeData?.lastname} 
                ${process.employeeData?.firstname}`),
          },
        },
      }) as GraphQLResult<CreateProcessRequestMutation>).data?.createProcessRequest;

      // If FN does not have a company association but a company
      // has been selected, create an association
      if (selectedCompany?.id
        && selectedNational
        && !selectedNational.companyID) {
        await client.graphql({
          query: updateForeignNational,
          variables: {
            input: {
              id: selectedNational!.id,
              companyID: selectedCompany.id,
            },
          },
        });
      }

      if (!isExistingJob && selectedJob) {
        await client.graphql({
          query: createJobDescription,
          variables: {
            input: {
              ...selectedJob,
              archived: ArchivedState.ACTIVE,
              type: JobDescriptionType.ONEOFF,
              id: oneOffJobID,
              foreignNationalID: foreignNationalID
          || process.employeeData?.tempID,
              companyID: selectedNational?.companyID || companyID,
            },
          },
        });
      }

      setNewItem({ ...newProcess, type: 'ProcessRequest' });
      setIsSaving(false);
      setOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      setIsSaving(false);
      setAlert(['Something went wrong initiating the case.', 'error']);
    }
  };

  const isShortTermGlobalProcess = process.requestedProcess === VisaStatus.GLOBAL_PROCESS
    && process.globalProcessDuration === GlobalProcessDuration.SHORT_TERM_BUSINESS_VISA;
  const isLongTermGlobalProcess = process.requestedProcess === VisaStatus.GLOBAL_PROCESS
    && process.globalProcessDuration === GlobalProcessDuration.LONG_TERM_EMPLOYMENT;

  const canSubmitNational = currentTab === 1 ? (
    process.employeeData?.firstname
    && process.employeeData?.lastname
    && process.employeeData?.email
    && isValidEmail(process.employeeData?.email)
    && !emailIsTaken
  ) : (!!selectedNational?.id);
  // Kept separate from the sponsorship rules below because those are skipped
  // entirely for individually sponsored requests, and GLOBAL_PROCESS is an
  // individual process - hanging the global fields off that branch let a
  // request through with no duration, country, visa type or co-counsel.
  // Which fields are mandatory follows what GlobalProcessFields actually
  // renders: country + visa type for short term, co-counsel for long term.
  const canSubmitGlobalProcess = process.requestedProcess !== VisaStatus.GLOBAL_PROCESS
    || (isShortTermGlobalProcess
      ? !!(process.globalProcessCountryCode && process.globalVisaType)
      : isLongTermGlobalProcess && !!process.vendorID);

  const canSubmitSponsorship = isShortTermGlobalProcess
    ? (selectedNational?.companyID || process.companyID)
    : (
      !titleIsTaken
      && (selectedNational?.companyID || process.companyID)
      && process.employmentData?.salary?.amount
      && process.employmentData?.location?.type
      && isExistingJob ? process.jobDescriptionID : selectedJob?.title
      && (currentTab === 1 ? process.employmentData?.startDate : true)
    );

  const canSubmit = !!(
    canSubmitNational
    && process.requestedProcess
    && process.individualSponsored !== null
    && process.individualSponsored !== undefined
    && canSubmitGlobalProcess
    && (
      process.individualSponsored
        ? true
        : canSubmitSponsorship && titleIsTaken === false && emailIsTaken === false
    )
  );

  const employeeTabs: Array<Tab> = [
    {
      title: 'Existing Individual',
      component: ExistingIndividual,
      icon: React.createElement(AccountCircleIcon),
    },
    {
      title: 'New Individual',
      component: NewIndividual,
      icon: React.createElement(AccountCircleIcon),
    },
  ];

  // Per this spreadsheet: https://docs.google.com/spreadsheets/d/1kYpXtSfKChL-Ihu3mD50Dgfmt6ruE5egbqp58azU1PQ/edit#gid=1895049244

  const INDIVIDUAL_PROCESSES = [
    VisaStatus.H_4,
    VisaStatus.E_3D,
    VisaStatus.E_3D_CONSULATE,
    VisaStatus.H4_CONSULATE,
    VisaStatus.TD,
    VisaStatus.TD_MEXICO,
    VisaStatus.TD_CANADA,
    VisaStatus.USCIS_DEPENDENT,
    VisaStatus.EAD_DEPENDENT,
    VisaStatus.USCIS_PRIMARY_2,
    VisaStatus.GLOBAL_PROCESS,
  ];

  const COMPANY_PROCESSES = [
    VisaStatus.H_1B,
    VisaStatus.PERM_GREEN_CARD,
    // VisaStatus.H_1B_LOTTERY,
    // VisaStatus.H_1B_24_LOTTERY,
    // VisaStatus.H_1B_25_LOTTERY,
    VisaStatus.H_1B_26_LOTTERY,
    VisaStatus.E_3,
    VisaStatus.E_3_CONSULATE,
    VisaStatus.H_1B1,
    VisaStatus.H_1B1_CONSULATE,
    VisaStatus.TN,
    VisaStatus.TN_MEXICO,
    VisaStatus.TN_CANADA,
    VisaStatus.USCIS_PRIMARY,
    VisaStatus.AOS,
    VisaStatus.EAD_PRIMARY,
    VisaStatus.GLOBAL_PROCESS,
  ];

  useEffect(() => {
    setProcess(initialProcess);
  }, [open]);

  return (
    <CreateProcessContext.Provider
      value={{
        process,
        setProcess,
        isExistingJob,
        resume,
        selectedCompany,
        selectedJob,
        selectedNational,
        setIsExistingJob,
        setResume,
        setSelectedJob,
        setTitleIsTaken,
        titleIsTaken,
        emailIsTaken,
        setEmailIsTaken,
        // The following fields do not apply to case initiation
        // but are required for the context.
        activePanel: 'start',
        goToPanel: () => {},
        panelNames: [],
        setActivePanel: () => {},
        setSelectedCompany: () => {},
        setUploadedDescription,
        uploadedDescription,
      }}
    >
      <Modal
        open={open}
        setOpen={setOpen}
        title={DICTIONARY.CreateNewCaseTitle}
      >
        <Box sx={styles.sectionTitle}>
          <PersonIcon sx={styles.titleIcon} fontSize="large" />
          <Typography component="h3" variant="h5">
            {DICTIONARY.SelectIndividual}
          </Typography>
        </Box>
        <Box>
          <Tabs
            tabs={employeeTabs}
            label="New or existing employee"
            spreadTabs
            currentTabOverride={currentTab}
            setCurrentTabOverride={setCurrentTab}
          />
        </Box>

        {(selectedNational || (
          process.employeeData?.firstname
          && process.employeeData?.lastname
          && process.employeeData?.email
        )) && (
          <SponsorshipType />
        )}

        {process.individualSponsored !== null && (
          <ProcessType
            processOptions={
              process.individualSponsored
                ? INDIVIDUAL_PROCESSES
                : COMPANY_PROCESSES
            }
          />
        )}

        {process.requestedProcess === VisaStatus.GLOBAL_PROCESS && (
          <GlobalProcessFields />
        )}

        {process.requestedProcess && !process.individualSponsored && (
          <EmploymentFields />
        )}

        {(selectedNational?.companyID || process.companyID)
          && process.employmentData?.salary
          && process.employmentData?.location?.type
          && (// Only for New Individuals
            (currentTab === 1 ? process.employmentData.startDate : true)
          )
          && !isShortTermGlobalProcess && (
          <JobFields />
        )}

        <Box>
          {((isExistingJob && process.jobDescriptionID)
            || (!isExistingJob && selectedJob?.title)) && (
            <>
              <IconRow>
                <IconRowCell type="icon">
                  <IconCircle Icon={HistoryIcon} />
                </IconRowCell>
                <IconRowCell type="text">
                  <Typography variant="h6">
                    Premium Processing
                  </Typography>
                </IconRowCell>
              </IconRow>

              <Box sx={{ mb: 4 }}>
                <RadioGroup
                  aria-labelledby="existing-job-type"
                  name="existing-job-type"
                  value={String(process?.employmentData?.premiumProcessing)}
                  onChange={handleAddPremiumProcessingChange}
                >
                  <Grid container justifyContent="space-around">
                    <Grid>
                      <CardBase>
                        <FormControlLabel
                          value="true"
                          control={
                            <Radio color="primary" />
                          }
                          labelPlacement="top"
                          label="Yes"
                        />
                      </CardBase>
                    </Grid>

                    <Grid>
                      <CardBase>
                        <FormControlLabel
                          value="false"
                          control={
                            <Radio color="primary" />
                          }
                          labelPlacement="top"
                          label="No"
                        />
                      </CardBase>
                    </Grid>
                  </Grid>
                </RadioGroup>
              </Box>

              <IconRow>
                <IconRowCell type="icon">
                  <IconCircle Icon={EditIcon} />
                </IconRowCell>
                <IconRowCell type="text">
                  <Typography variant="h6">
                    Add Internal Notes
                  </Typography>
                </IconRowCell>
              </IconRow>

              <IconRow>
                <IconRowCell type="text">
                  <FormControl style={{ width: '100%' }}>
                    <InputLabel htmlFor="special-notes-input">Enter Notes</InputLabel>
                    <Input
                      id="special-notes-input"
                      value={process.employmentData?.notes || ''}
                      onChange={handleNotesChange}
                      multiline
                    />
                  </FormControl>
                </IconRowCell>
              </IconRow>

            </>
          )}
          {(isSaving ? <CircularProgress sx={styles.button} /> : (
            <Button
              disabled={!canSubmit}
              sx={styles.button}
              color="primary"
              onClick={handleSave}
              variant="contained"
            >
              SUBMIT CASE REQUEST
            </Button>
          ))}
        </Box>
      </Modal>
    </CreateProcessContext.Provider>
  );
};

export default InitiateCaseModal;
